/**
 * Admin review surface for a single digital-form submission.
 *
 * Route: /employees/:employeeId/forms/:assignmentId
 *
 * Displays the form schema rendered with the submitted values
 * (SSN values masked). Three actions:
 *
 *   * Mark as Reviewed   — flips status to ``reviewed``, notifies employee.
 *   * Request Revision   — sends back with a note; employee gets email
 *                          + in-app notif and the form reopens for edit.
 *   * Download PDF       — opens the auto-generated PDF snapshot.
 *
 * Submission metadata (date / IP) appears at the top.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Download,
  Loader2,
  Send,
} from "lucide-react";
import { pageTransition, formatDate } from "@/lib/utils";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/hooks/useToast";
import { usePermissions } from "@/hooks/usePermissions";
import { FormRenderer } from "@/components/forms/FormRenderer";
import {
  getAdminFormSubmission,
  markFormReviewed,
  requestFormRevision,
  type AdminFormSubmissionView,
} from "@/api/documentAssignments";
import type { FormField } from "@/components/forms/types";

export function EmployeeFormSubmissionPage() {
  const { employeeId, assignmentId } = useParams<{
    employeeId: string;
    assignmentId: string;
  }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { can } = usePermissions();
  const canManage = can("manage_documents");

  const [view, setView] = useState<AdminFormSubmissionView | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [revisionOpen, setRevisionOpen] = useState(false);
  const [revisionNote, setRevisionNote] = useState("");

  const load = async () => {
    if (!assignmentId) return;
    setLoading(true);
    try {
      setView(await getAdminFormSubmission(assignmentId));
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast(
        "error",
        ax.response?.data?.detail || "Couldn't load this submission",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId]);

  const schema = useMemo<FormField[]>(
    () => (view?.form_schema as unknown as FormField[]) ?? [],
    [view],
  );

  const onMarkReviewed = async () => {
    if (!assignmentId) return;
    setBusy(true);
    try {
      await markFormReviewed(assignmentId);
      addToast("success", "Marked as reviewed");
      await load();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast(
        "error",
        ax.response?.data?.detail || "Couldn't mark as reviewed",
      );
    } finally {
      setBusy(false);
    }
  };

  const onRequestRevision = async () => {
    if (!assignmentId || !revisionNote.trim()) return;
    setBusy(true);
    try {
      await requestFormRevision(assignmentId, revisionNote.trim());
      addToast("success", "Sent back to the employee");
      setRevisionOpen(false);
      setRevisionNote("");
      await load();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast(
        "error",
        ax.response?.data?.detail || "Couldn't send revision request",
      );
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="py-10 text-center text-text-muted">
        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
      </div>
    );
  }

  if (!view) {
    return (
      <motion.div {...pageTransition}>
        <Link
          to={employeeId ? `/employees/${employeeId}` : "/employees"}
          className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Link>
        <Card>
          <div className="py-10 text-center">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 text-danger" />
            <p className="text-sm text-text-primary font-medium">
              Submission not found.
            </p>
          </div>
        </Card>
      </motion.div>
    );
  }

  const employeeProfileLink = `/employees/${view.employee_id}`;
  const submittedAt = view.form_submitted_at;
  const isReviewed = view.status === "reviewed";
  const isRevisionRequested = view.status === "revision_requested";
  const isSubmitted = view.status === "signed" || isReviewed;

  return (
    <motion.div {...pageTransition}>
      <Link
        to={employeeProfileLink}
        className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to profile
      </Link>

      <div className="mb-6">
        <h2 className="text-2xl font-semibold font-heading text-text-primary">
          {view.template_name}
        </h2>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          {isReviewed && <Badge variant="success">Reviewed</Badge>}
          {!isReviewed && isSubmitted && (
            <Badge variant="info">Submitted · awaiting review</Badge>
          )}
          {isRevisionRequested && (
            <Badge variant="warning">Revision requested</Badge>
          )}
          {submittedAt && (
            <span className="text-xs text-text-muted">
              Submitted {formatDate(submittedAt)}
              {view.form_ip_address ? ` · IP ${view.form_ip_address}` : ""}
            </span>
          )}
        </div>
      </div>

      {/* Revision banner — visible whenever there's an unresolved
          revision note (the admin already sent it back). */}
      {isRevisionRequested && view.revision_note && (
        <Card className="mb-4 border-warning/40 bg-warning/5">
          <p className="text-sm text-text-primary font-medium">
            Revision requested
          </p>
          <p className="text-sm text-text-muted mt-1">{view.revision_note}</p>
          <p className="text-xs text-text-muted mt-2">
            The employee can edit and resubmit. They&rsquo;ve been notified
            by email.
          </p>
        </Card>
      )}

      <Card className="mb-4">
        <CardHeader>
          <CardTitle>Submission</CardTitle>
          {view.form_pdf_url && (
            <a
              href={view.form_pdf_url}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-accent hover:underline inline-flex items-center gap-1"
            >
              <Download className="h-4 w-4" />
              Download PDF
            </a>
          )}
        </CardHeader>

        {schema.length === 0 ? (
          <p className="text-sm text-text-muted">
            This submission has no form fields. The template may have been
            edited after submission.
          </p>
        ) : (
          <FormRenderer
            schema={schema}
            data={view.form_data}
            onChange={() => undefined}
            disabled
          />
        )}
      </Card>

      {/* Admin actions — hidden when no review permission. */}
      {canManage && (
        <Card>
          <CardHeader>
            <CardTitle>Admin actions</CardTitle>
          </CardHeader>
          <p className="text-sm text-text-muted">
            {isReviewed
              ? "This submission has been reviewed. You can still send it back if you spot an issue."
              : isRevisionRequested
                ? "Waiting on the employee to resubmit. You can mark it reviewed once they do, or amend the note."
                : "Review the submission and either accept it or send it back with notes."}
          </p>
          <div className="mt-3 flex gap-2 flex-wrap">
            {!isReviewed && (
              <Button onClick={onMarkReviewed} isLoading={busy}>
                <CheckCircle2 className="h-4 w-4" />
                Mark as Reviewed
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() => setRevisionOpen(true)}
              disabled={busy}
            >
              <Send className="h-4 w-4" />
              Request Revision
            </Button>
            {view.form_pdf_url && (
              <Button
                variant="secondary"
                onClick={() => {
                  const a = document.createElement("a");
                  a.href = view.form_pdf_url!;
                  a.download = `${view.template_name}.pdf`;
                  a.rel = "noreferrer";
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                }}
              >
                <Download className="h-4 w-4" />
                Download PDF
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Request-revision modal. The note is required + has a 2000-char
          server cap, mirrored here. */}
      <Modal
        isOpen={revisionOpen}
        onClose={() => (busy ? null : setRevisionOpen(false))}
        title="Request revision"
      >
        <p className="text-sm text-text-muted">
          Tell {view.template_name ? "the employee" : "them"} what needs to
          change. They&rsquo;ll receive an email + in-app notification with
          this note, and the form reopens for editing.
        </p>
        <textarea
          value={revisionNote}
          onChange={(e) => setRevisionNote(e.target.value.slice(0, 2000))}
          rows={5}
          className="mt-3 block w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
          placeholder="e.g. The routing number doesn't match a US bank — please double-check."
          autoFocus
        />
        <div className="mt-1 text-[11px] text-text-muted text-right">
          {revisionNote.length} / 2000
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={() => setRevisionOpen(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            onClick={onRequestRevision}
            isLoading={busy}
            disabled={!revisionNote.trim()}
          >
            <Send className="h-4 w-4" />
            Send back to employee
          </Button>
        </div>
      </Modal>

      {/* Quick-link to the employee profile under the form for context. */}
      <p className="mt-4 text-xs text-text-muted">
        <Link
          to={employeeProfileLink}
          className="text-accent hover:underline"
        >
          ← Open employee profile
        </Link>{" "}
        — see other documents and onboarding status.
      </p>
      <span className="hidden">{navigate.length}</span>
    </motion.div>
  );
}

export default EmployeeFormSubmissionPage;
